import { Capacitor } from '@capacitor/core';

// Face ID / Touch ID: device-local unlock convenience only, never a security boundary —
// TOTP stays the sole real 2FA (docs/TODO.md §1a). @aparajita/capacitor-biometric-auth
// chosen over @capgo/capacitor-native-biometric specifically because it has zero
// credential-storage surface — this codebase already decided against Keychain-backed
// credential storage for the JWT (context/decisions/auth-and-security.md), so a plugin
// whose flagship feature is exactly that isn't worth the extra vendor/binary surface for
// a feature that's just "verify the person holding this phone, then get out of the way."
//
// authenticate() REJECTS (a BiometryError) on failure, cancellation, or no biometry
// enrolled — never resolves false. Every export here normalizes that into a boolean
// instead, since callers must never get stuck: a failed/cancelled/unavailable check falls
// through to showing the app as normal (see BiometricLockGate.js), by deliberate design.

export function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

// Wrapped in a plain object on purpose — NEVER return a Capacitor plugin proxy directly from
// an async function or resolve a promise with it. The proxy answers any property access with
// a function that forwards to native, INCLUDING `then`, so the promise machinery sees a
// thenable and calls `BiometricAuth.then(resolve, reject)`. That forwards to native as a bogus
// "then" method call, which rejects into an unrelated, never-awaited promise (a silent
// unhandled rejection, invisible in Xcode's console — only visible via Safari Web Inspector) —
// while resolve/reject for the REAL await are never invoked, so every caller hangs forever.
// This was the actual root cause of "no Face ID toggle ever appears" (2026-09-11), previously
// misdiagnosed as an entitlement/linking issue. Same bug, same fix, as
// nativeSocialAuth.js's ensureGoogleInitialized() — see its comment for the full mechanism.
// Pinned by biometricAuth.test.js.
async function loadPlugin() {
  const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
  return { plugin: BiometricAuth };
}

/** Whether this device currently has a usable, enrolled biometric available. */
export async function checkBiometryAvailable() {
  if (!isNativePlatform()) return false;
  try {
    const { plugin: BiometricAuth } = await loadPlugin();
    const { isAvailable } = await BiometricAuth.checkBiometry();
    return !!isAvailable;
  } catch {
    return false;
  }
}

/**
 * Prompts Face ID/Touch ID. Resolves true only on a real success — every other outcome
 * (cancel, failure, lockout, no biometry enrolled) resolves false. Never rejects.
 */
export async function authenticateWithBiometrics(reason = 'Unlock Agence') {
  if (!isNativePlatform()) return false;
  try {
    const { plugin: BiometricAuth } = await loadPlugin();
    await BiometricAuth.authenticate({ reason, cancelTitle: 'Cancel' });
    return true;
  } catch {
    return false;
  }
}
